import { RegisterableHotkey } from '@tanstack/react-hotkeys';
import { pick } from 'lodash';
import { ASK, FlagName } from '@shared/util/featureFlags';
import { I18nKey } from '@shared/util/i18n';

/**
 * Union of valid action IDs.
 */
export type HotkeyActionId =
  | 'searchGlobal'
  | 'searchWorkspace'
  | 'saveCard'
  | 'openPreferences'
  | 'toggleRail'
  | 'createNewWorkflow'
  | 'showActivity'
  | 'showMentions'
  | 'createCard'
  | 'showViewSelector'
  | 'showMembers'
  | 'addMembers'
  | 'showWorkspaceDetails'
  | 'showWorkspaceReports'
  | 'toggleFavorite'
  | 'layoutChat'
  | 'layoutSplit'
  | 'layoutWorkflow'
  | 'prevCard'
  | 'nextCard'
  | 'toggleChat'
  | 'toggleFixedWidth'
  | 'askGlobal'
  | 'askWorkspace'
  | 'askCard';

/** Categories of actions for the Preferences page. */
export type HotkeyCategory = 'app' | 'workspace' | 'cardView';

export interface HotkeyAction {
  id: HotkeyActionId;
  label: I18nKey;
  category: HotkeyCategory;
  defaultHotkey?: RegisterableHotkey;
  /**
   * Reserved keys are blocked from the browser app-wide (e.g. ⌘S never opens
   * "Save Page"), even while unbound. See `ReservedHotkeys` component.
   */
  reserved?: boolean;
  /**
   * Hides the action's row on the Preferences page if the flag is disabled.
   */
  featureFlag?: FlagName;
}

export interface ReservedHotkeyAction extends HotkeyAction {
  reserved: true;
  defaultHotkey: RegisterableHotkey;
}

/**
 * Requires an entry for every `HotkeyActionId`, and pins each entry's `id` to
 * its key, so the union and the definitions below cannot drift apart.
 */
type HotkeyActionsById = { [K in HotkeyActionId]: HotkeyAction & { id: K } };

/**
 * Hotkey actions with their default hotkeys, keyed by id.
 */
export const HOTKEY_ACTIONS: HotkeyActionsById = {
  askGlobal: {
    id: 'askGlobal',
    label: 'preferences.hotKeys.actions.askGlobal',
    category: 'app',
    defaultHotkey: 'Mod+Y',
    featureFlag: ASK,
  },
  searchGlobal: {
    id: 'searchGlobal',
    label: 'preferences.hotKeys.actions.searchGlobal',
    category: 'app',
    defaultHotkey: 'Mod+K',
  },
  toggleRail: {
    id: 'toggleRail',
    label: 'preferences.hotKeys.actions.toggleRail',
    category: 'app',
    defaultHotkey: 'Mod+\\',
  },
  createNewWorkflow: {
    id: 'createNewWorkflow',
    label: 'preferences.hotKeys.actions.createNewWorkflow',
    category: 'app',
    defaultHotkey: 'Shift+N',
  },
  showActivity: {
    id: 'showActivity',
    label: 'preferences.hotKeys.actions.showActivity',
    category: 'app',
    defaultHotkey: 'Shift+A',
  },
  showMentions: {
    id: 'showMentions',
    label: 'preferences.hotKeys.actions.showMentions',
    category: 'app',
    defaultHotkey: 'Shift+M',
  },
  openPreferences: {
    id: 'openPreferences',
    label: 'preferences.hotKeys.actions.openPreferences',
    category: 'app',
    defaultHotkey: 'Mod+Alt+,',
  },
  askWorkspace: {
    id: 'askWorkspace',
    label: 'preferences.hotKeys.actions.askWorkspace',
    category: 'workspace',
    defaultHotkey: 'Mod+Shift+Y',
    featureFlag: ASK,
  },
  searchWorkspace: {
    id: 'searchWorkspace',
    label: 'preferences.hotKeys.actions.searchWorkspace',
    category: 'workspace',
    defaultHotkey: 'Mod+Shift+K',
  },
  createCard: {
    id: 'createCard',
    label: 'preferences.hotKeys.actions.createCard',
    category: 'workspace',
    defaultHotkey: 'N',
  },
  showViewSelector: {
    id: 'showViewSelector',
    label: 'preferences.hotKeys.actions.showViewSelector',
    category: 'workspace',
    defaultHotkey: 'V',
  },
  showMembers: {
    id: 'showMembers',
    label: 'preferences.hotKeys.actions.showMembers',
    category: 'workspace',
    defaultHotkey: 'M',
  },
  addMembers: {
    id: 'addMembers',
    label: 'preferences.hotKeys.actions.addMembers',
    category: 'workspace',
    defaultHotkey: 'A',
  },
  showWorkspaceDetails: {
    id: 'showWorkspaceDetails',
    label: 'preferences.hotKeys.actions.showWorkspaceDetails',
    category: 'workspace',
    defaultHotkey: 'I',
  },
  showWorkspaceReports: {
    id: 'showWorkspaceReports',
    label: 'preferences.hotKeys.actions.showWorkspaceReports',
    category: 'workspace',
  },
  toggleFavorite: {
    id: 'toggleFavorite',
    label: 'preferences.hotKeys.actions.toggleFavorite',
    category: 'workspace',
  },
  layoutChat: {
    id: 'layoutChat',
    label: 'preferences.hotKeys.actions.layoutChat',
    category: 'workspace',
    defaultHotkey: 'Mod+6',
  },
  layoutSplit: {
    id: 'layoutSplit',
    label: 'preferences.hotKeys.actions.layoutSplit',
    category: 'workspace',
    defaultHotkey: 'Mod+7',
  },
  layoutWorkflow: {
    id: 'layoutWorkflow',
    label: 'preferences.hotKeys.actions.layoutWorkflow',
    category: 'workspace',
    defaultHotkey: 'Mod+8',
  },
  askCard: {
    id: 'askCard',
    label: 'preferences.hotKeys.actions.askCard',
    category: 'cardView',
    defaultHotkey: 'Mod+Shift+Y',
    featureFlag: ASK,
  },
  prevCard: {
    id: 'prevCard',
    label: 'preferences.hotKeys.actions.prevCard',
    category: 'cardView',
    defaultHotkey: 'Mod+J',
  },
  nextCard: {
    id: 'nextCard',
    label: 'preferences.hotKeys.actions.nextCard',
    category: 'cardView',
    defaultHotkey: 'Mod+K',
  },
  toggleChat: {
    id: 'toggleChat',
    label: 'preferences.hotKeys.actions.toggleChat',
    category: 'cardView',
    defaultHotkey: 'Mod+\\',
  },
  toggleFixedWidth: {
    id: 'toggleFixedWidth',
    label: 'preferences.hotKeys.actions.toggleFixedWidth',
    category: 'cardView',
    defaultHotkey: 'Mod+Shift+F',
  },
  saveCard: {
    id: 'saveCard',
    label: 'preferences.hotKeys.actions.saveCard',
    category: 'cardView',
    defaultHotkey: 'Mod+S',
    reserved: true,
  },
};

/** A subset of the hotkey actions, keyed by id. */
export type HotkeyActionsMap = Partial<Record<HotkeyActionId, HotkeyAction>>;

const BASIC_HOTKEY_ACTION_IDS: HotkeyActionId[] = [
  'saveCard',
  'searchGlobal',
  'toggleRail',
  'prevCard',
  'nextCard',
];

/**
 * The fixed set of hotkeys available while the `hot-keys-customization` flag
 * is off. These actions keep their default bindings; everything else is
 * unbound, and the Preferences page shows this set read-only. Use
 * `useHotkeyActions` to resolve the set for the current experience.
 */
export const BASIC_HOTKEY_ACTIONS: HotkeyActionsMap = pick(HOTKEY_ACTIONS, BASIC_HOTKEY_ACTION_IDS);
